import { useEffect, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import * as Sentry from "@sentry/react";
import { Outlet, useLocation, useNavigate } from "react-router";

import {
  AppGlobalSearchCommandPalette,
  Center,
  ErrorText,
  KeyboardShortcutsModal,
  LoadingIndicator,
} from "@/components";
import { CandidateCallRequestBanner } from "@/components/call-requests/CandidateCallRequestBanner";
import { CornerPromptStack } from "@/components/corner-prompts/CornerPromptStack";
import { QuickAccessModal } from "@/components/quick-access/QuickAccessModal";
import { AppSidebar } from "@/components/sidebar/AppSidebar";
import { SIDEBAR_COOKIE_NAME, SidebarProvider } from "@/components/ui/sidebar";

import { PinnedJobsProvider } from "@/contexts/PinnedJobsContext";
import { TaggableUsersProvider } from "@/contexts/TaggableUsersContext";
import { UnreadNotificationsProvider } from "@/contexts/UnreadNotificationsContext";
import { useGetCompaniesSummary } from "@/hooks/queries/useGetCompaniesSummary";
import { useTrackLinkAttribution } from "@/hooks/useTrackLinkAttribution";
import { cookies } from "@/lib/cookies";
import { storage } from "@/lib/storage";
import { useUpdateSetUser } from "@/hooks/updateSets/useUpdateSetUser";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";

const ACTIVITY_HEARTBEAT_INTERVAL_MS = 60_000;

export const AppLayout = () => {
  // TODO: Should this logic be put in a separate provider/layer?
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const currentUserId = useGetCurrentUser();
  /* Database CRUD Operations */
  // Read
  const { isPending, isError } = useGetCompaniesSummary({
    staleTime: Infinity,
  });
  const { mutate: updateUser } = useUpdateSetUser();
  const lastActivityAtRef = useRef<number | null>(Date.now());
  // Search params are part of the page identity — the job tab lives in `?t=`,
  // and presence indicators key off it. Kept in a ref so the heartbeat interval
  // isn't torn down on every navigation.
  const lastPageRef = useRef(`${pathname}${search}`);
  lastPageRef.current = `${pathname}${search}`;

  /* Misc Hooks */

  useTrackLinkAttribution();

  /* React Hooks */
  // useState
  const [checkedAuth, setCheckedAuth] = useState(false);

  // useEffect
  useEffect(() => {
    try {
      const user = storage.getUser();
      if (!user) {
        // TODO: Should we also call sign out here?
        storage.clear();
        void navigate("/sign-in");
      } else {
        try {
          // Identify the user in Mixpanel if they are signed in. This mirrors the implementation on the talent side.
          mixpanel.identify(user.id.toString());
          Sentry.setUser(user);
        } catch (error) {
          Sentry.captureException(
            `Error setting user on mixpanel or sentry: ${error as Error}`,
          );
        }
        setCheckedAuth(true);
      }
    } catch {
      // TODO: Should we also call sign out here?
      storage.clear();
      void navigate("/sign-in");
    }
  }, [navigate]);

  // Automatically scroll to top when navigating between routes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Loop that watches user activity and updates their activity.
  // This empowers when to send push noti's if a user is AFK
  // Also empowers the live pipeline tracker
  useEffect(() => {
    const handlePageHide = () => {
      lastActivityAtRef.current = null;
    };
    window.addEventListener("pagehide", handlePageHide);

    // Raw window-level input is the activity signal — scrolling, mouse
    // movement, clicks, and typing all keep the idle clock running without
    // callers wiring anything. Throttled so high-frequency mousemove/scroll
    // handlers stay cheap.
    const INPUT_THROTTLE_MS = 10_000;
    const handleUserInput = () => {
      const now = Date.now();

      const gapMs = now - (lastActivityAtRef.current ?? 0);
      // Throttle input tracking
      if (gapMs < INPUT_THROTTLE_MS) return;
      lastActivityAtRef.current = now;
    };
    const INPUT_EVENTS = [
      "focus",
      "keydown",
      "mousemove",
      "pointerdown",
      "scroll",
      "touchstart",
      "wheel",
    ] as const;
    INPUT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserInput, {
        capture: true,
        passive: true,
      });
    });

    // Tab switching never fires pagehide, so visibility is its own signal:
    // hiding the tab means AFK, revealing it means back at the keyboard.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastActivityAtRef.current = null;
        return;
      }
      lastActivityAtRef.current = Date.now();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      INPUT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserInput, {
          capture: true,
        });
      });
    };
  }, []);

  // Send activity updates — skipped locally so dev sessions don't mark us active.
  useEffect(() => {
    if (import.meta.env.DEV || !currentUserId?.id) return;

    const sendHeartbeat = () => {
      // Background tabs are AFK by definition — don't burn a request on them.
      if (document.hidden) return;

      const lastActivityAt = lastActivityAtRef.current;
      updateUser({
        id: currentUserId.id,
        updateInternalUserRequest: {
          lastActiveAt: lastActivityAt
            ? new Date(lastActivityAt).toISOString()
            : undefined,
          lastPage: lastPageRef.current,
        },
      });
    };
    sendHeartbeat();
    const intervalId = setInterval(
      sendHeartbeat,
      ACTIVITY_HEARTBEAT_INTERVAL_MS,
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [currentUserId?.id, updateUser]);

  /* Helpers */
  const defaultOpen =
    // We only want the sidebar to be closed by default if the cookie value is
    // explicitly set to "false". Otherwise, we want the sidebar to be open by
    // default.
    cookies.get(SIDEBAR_COOKIE_NAME) === "false" ? false : true;

  /* Render */
  const render = () => {
    if (isPending || !checkedAuth) {
      return (
        <Center className="h-screen">
          <LoadingIndicator variant="xlarge" />
        </Center>
      );
    }
    if (isError) {
      return (
        <Center className="h-screen">
          <ErrorText errorMessage="An error occurred while loading companies. Please let the engineers know if they are not already on it!" />
        </Center>
      );
    }

    return (
      <TaggableUsersProvider>
        <UnreadNotificationsProvider>
          <PinnedJobsProvider>
            <SidebarProvider defaultOpen={defaultOpen}>
              <AppSidebar />
              <main className="w-full overflow-x-auto">
                <Outlet />
              </main>
              <AppGlobalSearchCommandPalette />
              <QuickAccessModal />
              <KeyboardShortcutsModal />
              <CornerPromptStack />
              <CandidateCallRequestBanner />
            </SidebarProvider>
          </PinnedJobsProvider>
        </UnreadNotificationsProvider>
      </TaggableUsersProvider>
    );
  };

  return render();
};
